import { c as _c } from "react/compiler-runtime";
import { useUser } from "@auth0/nextjs-auth0/client";
import { useNinetailed } from "@ninetailed/experience.js-react";
import type { AccountCustomerOutputDto } from "@sticker-giant/sg-frontend-common";
import { type RefObject, useEffect, useRef } from "react";
import { buildAuthenticatedCustomerTraits, getAnonymousId, resetIdentityIfStale } from "@/lib/analytics/segment";
import { useCustomer } from "@/queries/useCustomer";
import { useAuthClaimsStore } from "@/stores/useAuthClaimsStore";

// What already went out for a customer. As in the Segment sync, `withPii: false` marks
// the id-only fallback and is deliberately not terminal — on signup the account record
// is often unreadable on the first lookup, and the traits still have to follow.
type SentIdentity = {
  customerId: string;
  withPii: boolean;
};

// Logout is a full-page navigation to /auth/logout, so the mount that identified the
// customer never sees them leave, while Ninetailed's profile id survives in its cookie.
// This marker carries "this browser's profile belongs to a customer" across page loads.
const IDENTIFIED_MARKER = "nt_identified_customer";
function readMarker() {
  try {
    return localStorage.getItem(IDENTIFIED_MARKER);
  } catch {
    return null;
  }
}
function writeMarker(customerId: string | undefined) {
  try {
    if (customerId) localStorage.setItem(IDENTIFIED_MARKER, customerId);else localStorage.removeItem(IDENTIFIED_MARKER);
  } catch {
    // Storage blocked: the in-mount `sent` ref still covers same-page logouts.
  }
}
function identifyQuietly(ninetailed: ReturnType<typeof useNinetailed>, ...args: Parameters<ReturnType<typeof useNinetailed>["identify"]>) {
  ninetailed.identify(...args).catch(() => {
    // Best effort: the next page load identifies again.
  });
}
type SendIdentityInput = {
  ninetailed: ReturnType<typeof useNinetailed>;
  customerId: string;
  sent: SentIdentity | undefined;
  customer?: AccountCustomerOutputDto;
  accountFailed?: boolean;
};
function sendIdentity({
  ninetailed,
  customerId,
  sent,
  customer,
  accountFailed
}: SendIdentityInput) {
  const alreadySent = sent?.customerId === customerId;
  if (alreadySent && sent?.withPii) return undefined;
  if (customer) {
    identifyQuietly(ninetailed, customerId, buildAuthenticatedCustomerTraits(customer));
    writeMarker(customerId);
    return {
      customerId,
      withPii: true
    };
  }
  if (accountFailed && !alreadySent) {
    identifyQuietly(ninetailed, customerId);
    writeMarker(customerId);
    return {
      customerId,
      withPii: false
    };
  }
  return undefined;
}

// The Segment anonymousId this browser's Ninetailed profile is currently aliased to,
// plus the claim still in flight — resolving the id is async, so without this two
// effect runs would race and alias twice.
type GuestAlias = {
  id?: string;
  inFlight?: Promise<void>;
};

// Segment's destination keys a guest's traits and Engage audiences by Segment's
// anonymousId, so the browser's Ninetailed profile has to claim that same id as its
// alias. Without it, the traits land on a profile no session is attached to and nothing
// personalizes before login.
async function syncGuestProfile(ninetailed: ReturnType<typeof useNinetailed>, sent: RefObject<SentIdentity | undefined>, guest: GuestAlias) {
  // Only reset a profile we actually identified — resetting on every guest page load
  // would churn the anonymous profile personalization depends on.
  if (sent.current || readMarker()) {
    sent.current = undefined;
    guest.id = undefined;
    writeMarker(undefined);
    await ninetailed.reset();
  }

  // Segment's reset mints a new anonymousId; reading it before that would alias the
  // id Segment still associates with the departed customer.
  await resetIdentityIfStale();
  const anonymousId = await getAnonymousId();
  if (!anonymousId || guest.id === anonymousId) return;
  guest.id = anonymousId;
  await ninetailed.identify(anonymousId);
}
function startGuestSync(ninetailed: ReturnType<typeof useNinetailed>, sent: RefObject<SentIdentity | undefined>, guestRef: RefObject<GuestAlias>) {
  const guest = guestRef.current;
  if (guest.inFlight) return;
  guest.inFlight = syncGuestProfile(ninetailed, sent, guest).catch(() => {
    // Best effort: the next page load aliases again.
  }).finally(() => {
    guest.inFlight = undefined;
  });
}

// Mirrors the Segment identity into Ninetailed by claiming the same two keys Segment's
// destination writes under: the anonymousId while the visitor is a guest, then the
// customer id once they authenticate. Ninetailed merges profiles that share an alias, so
// aliasing to the anonymousId first and the customer id second chains the two.
//
// Deliberately a sibling of useSyncSegmentIdentity rather than an addition to it: the
// two run independently, so a Ninetailed failure can never regress Segment attribution.
export function useSyncNinetailedIdentity() {
  const $ = _c(9);
  const ninetailed = useNinetailed();
  const {
    user,
    isLoading
  } = useUser();
  const customerId = useAuthClaimsStore(_temp);
  const {
    data: customer,
    isError
  } = useCustomer();
  const sent = useRef(undefined);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const guest = useRef(t0);
  let t1;
  let t2;
  if ($[1] !== customer || $[2] !== customerId || $[3] !== isError || $[4] !== isLoading || $[5] !== ninetailed || $[6] !== user) {
    t1 = () => {
      if (isLoading) {
        return;
      }
      if (!user) {
        startGuestSync(ninetailed, sent, guest);
        return;
      }
      if (!customerId) {
        return;
      }
      const next = sendIdentity({
        ninetailed,
        customerId,
        sent: sent.current,
        customer,
        accountFailed: isError
      });
      if (next) {
        sent.current = next;
      }
    };
    t2 = [ninetailed, isLoading, user, customerId, customer, isError];
    $[1] = customer;
    $[2] = customerId;
    $[3] = isError;
    $[4] = isLoading;
    $[5] = ninetailed;
    $[6] = user;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useEffect(t1, t2);
}
function _temp(state) {
  return state.claims?.sg_customer_id;
}